import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import SmsIcon from "src/assets/icons/sms"
import { useSelector } from "react-redux"
import useSupport from "src/hooks/useSupport"

const ContactInfo = ({ control: externalControl, errors: externalErrors }) => {
  const [issueType, setIssueType] = useState(null)
  const { ownDetails } = useSelector((state) => state.profile)
  const { isContactSupportLoading } = useSelector((state) => state.support)
  const { handleContactSupport } = useSupport()

  // State for form field values
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contact: "",
    message: "",
  })

  // Update form data from ownDetails when it changes
  useEffect(() => {
    if (ownDetails?.details) {
      const userDetails = ownDetails.details
      setFormData((prev) => ({
        ...prev,
        firstName: userDetails.firstName || "",
        lastName: userDetails.lastName || "",
        email: userDetails.email || "",
        contact: userDetails.contact || "",
      }))

      // Also update the form control values
      setValue("firstName", userDetails.firstName || "")
      setValue("lastName", userDetails.lastName || "")
      setValue("email", userDetails.email || "")
      setValue("contact", userDetails.contact || "")
    }
  }, [ownDetails])

  const {
    control: localControl,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors: localErrors },
  } = useForm({
    defaultValues: formData, // Use our state-managed values
  })

  // Handle input changes - now only the message field will use this
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
    setValue(field, value)
  }

  // Use the provided control and errors or the local ones
  const activeControl = externalControl || localControl
  const activeErrors = externalErrors || localErrors

  const onSubmit = async (data) => {
    try {
      const messageToSend = formData.message
      // console.log("Sending message:", messageToSend)

      if (messageToSend && messageToSend.trim() !== "") {
        await handleContactSupport({
          message: messageToSend,
        })

        // Clear just the message
        handleInputChange("message", "")
      } else {
        alert("Please enter a message before submitting")
      }
    } catch (error) {
      console.error("Failed to send message:", error)
    }
  }

  return (
    <form
      className="bg-white shadow-md rounded-2xl md:p-8 p-4 md:max-w-xl max-w-md mx-auto"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Header */}
      <h2 className="text-3xl font-normal text-[#005BB2] mb-2">Get in touch</h2>
      <p className="text-sm text-gray-600 mb-6">
        Our friendly team would love to hear from you.
      </p>

      {/* First and Last Name */}
      <div className="flex gap-4 mb-4">
        <div className="flex-1">
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="firstName"
          >
            First Name*
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            readOnly
            className="mt-1 p-3 w-full border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
          />
        </div>

        <div className="flex-1">
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="lastName"
          >
            Last Name*
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            readOnly
            className="mt-1 p-3 w-full border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
          />
        </div>
      </div>

      {/* Email */}
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700"
          htmlFor="email"
        >
          Email*
        </label>
        <div className="relative">
          <span className="absolute inset-y-0 left-3 flex items-center">
            <SmsIcon />
          </span>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            readOnly
            className="p-3 pl-10 w-full border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
          />
        </div>
      </div>

      {/* Contact */}
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700"
          htmlFor="contact"
        >
          Contact*
        </label>
        <div className="flex gap-2">
          <select
            className="w-1/4 p-3 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
            defaultValue="IN"
            disabled
          >
            <option value="IN">IN</option>
            <option value="US">US</option>
          </select>
          <input
            type="tel"
            id="contact"
            name="contact"
            value={formData.contact}
            readOnly
            className="flex-1 p-3 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
          />
        </div>
      </div>

      {/* Message - this remains editable */}
      <div className="mb-6">
        <label
          className="block text-sm font-medium text-gray-700"
          htmlFor="message"
        >
          Message*
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={(e) => handleInputChange("message", e.target.value)}
          placeholder="Enter a description..."
          className="mt-1 p-3 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#005BB2]"
          rows={4}
        />
        {activeErrors.message && (
          <p className="text-red-500 text-xs mt-1">
            {activeErrors.message.message}
          </p>
        )}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isContactSupportLoading}
        className={`w-full bg-[#005BB2] text-white py-3 rounded-md text-sm font-medium hover:bg-[#004499] focus:outline-none focus:ring-2 focus:ring-[#005BB2] ${isContactSupportLoading ? "opacity-70 cursor-not-allowed" : ""}`}
      >
        {isContactSupportLoading ? "SENDING..." : "SEND"}
      </button>
    </form>
  )
}

export default ContactInfo
